// ============================================================================
// JoinMeetingPage — #join/<token> landing for online attendees (Task #15).
// ============================================================================
// Hit from the magic link in the confirmation email. The API endpoint at
// /api/registrations/:token/join either 302-redirects to the meeting URL
// (success) or returns a JSON error. We probe with a HEAD-style fetch using
// redirect: 'manual' so we can distinguish success (opaqueredirect) from
// error JSON without prematurely consuming the redirect.
//
// On success: navigate to the API URL so the browser follows the 302 →
// meeting URL. On error: render the message inline so the user knows why
// they can't join (too early, too late, cancelled, wrong format).
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react';

interface Props {
  token: string;
}

export function JoinMeetingPage({ token }: Props) {
  const [status, setStatus] = useState<'probing' | 'redirecting' | 'error'>('probing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const url = `/api/registrations/${encodeURIComponent(token)}/join`;
    let cancelled = false;

    (async () => {
      try {
        // redirect: 'manual' returns type='opaqueredirect' on 3xx, lets us
        // read the JSON body on 4xx/5xx without following anywhere.
        const probe = await fetch(url, { method: 'GET', redirect: 'manual' });
        if (cancelled) return;
        if (probe.type === 'opaqueredirect' || probe.status === 0) {
          // Success path: navigate to the API URL so the browser follows
          // the 302 to the meeting URL.
          setStatus('redirecting');
          window.location.href = url;
          return;
        }
        // Error path: parse JSON.
        try {
          const body = await probe.json();
          const msg = body?.error?.message || 'Could not join the meeting.';
          setErrorMessage(msg);
        } catch {
          setErrorMessage('Could not join the meeting.');
        }
        setStatus('error');
      } catch (err: any) {
        if (cancelled) return;
        setErrorMessage(err?.message || 'Network error connecting to the meeting.');
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
        </div>
      </header>
      <main className="flex-1 max-w-md mx-auto px-4 py-12 w-full">
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          {(status === 'probing' || status === 'redirecting') && (
            <>
              <Loader2 className="h-10 w-10 text-uif-green-dark mx-auto animate-spin" />
              <h1 className="text-lg font-bold text-uif-green-dark mt-3">
                {status === 'probing' ? 'Checking your registration…' : 'Connecting you to the meeting…'}
              </h1>
              <p className="text-sm text-gray-600 mt-2">
                You'll be redirected in a moment. If nothing happens,{' '}
                <a
                  href={`/api/registrations/${encodeURIComponent(token)}/join`}
                  className="text-uif-green-dark hover:underline inline-flex items-center gap-1"
                >
                  click here to join <ExternalLink className="h-3 w-3" />
                </a>.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="h-10 w-10 text-red-600 mx-auto" />
              <h1 className="text-xl font-bold text-red-800 mt-2">Can't join right now</h1>
              <p className="text-sm text-gray-700 mt-2">{errorMessage}</p>
              <a
                href="/"
                className="inline-block mt-4 rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Back to the calendar
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
